import { Application, DisplayObject, Component } from '@tencent/alloy-renderer';
import { IRenderRectConfig } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-mobile/type';
import { IEditorAppServices } from '@src/slide/application/entry/toc/editor/base/base-editor-app-service.interface';
import { IMobileScrollService } from '@src/slide/application/service/mobile-scroll-service/mobile-scroll-service.interface';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IMobileEditorAppServices } from '@src/slide/application/entry/toc/editor/mobile/mobile-editor-app-service.interface';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import { IPcScrollService } from '@src/slide/application/service/pc-scroll-service/pc-scroll-service.interface';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';

export class RenderArea {
    public application: Application<any>;
    public canvasDom: HTMLCanvasElement;
    public renderComponent: Component;
    private scale: number;
    private components: Array<DisplayObject>;
    private left: number;
    private top: number;
    private renderRectConfig: IRenderRectConfig;
    private isOffsetWithPage: boolean;

    public constructor(
        width: number,
        height: number,
        scale: number,
        application: Application,
        renderRectConfig?: IRenderRectConfig,
        isOffsetWithPage?: boolean,
    ) {
        this.scale = scale;
        this.isOffsetWithPage = !!isOffsetWithPage;
        const { scrollLeft = 0, scrollTop = 0 } = renderRectConfig || {};
        const { slideLeft, slideTop } = (AppServiceProvider.getInstance().getAppServices() as IEditorAppServices)
            .viewableServiceLayer.editAreaCanvasSizeManager.getCanvasSize();
        this.left = slideLeft + scrollLeft;
        this.top = slideTop + scrollTop;
        this.application = application;
        this.application.resolution = Math.ceil(window.devicePixelRatio);
        this.buildSlideComponent();
        this.updateInfo(width, height, scale);
        this.components = [];
        this.application.requestRender();
    }

    /**
     * 更新需要的一些信息
     * @param width
     * @param height
     * @param scale
     * @param renderRectConfig
     */
    public updateInfo(width: number, height: number, scale: number, renderRectConfig?: IRenderRectConfig): void {
        const roundWidth = Math.round(width);
        const roundHeight = Math.round(height);
        this.scale = scale;
        this.application.width = roundWidth;
        this.application.height = roundHeight;
        this.application.stage.transform.scaleX = scale;
        this.application.stage.transform.scaleY = scale;
        let { slideLeft, slideTop } = (AppServiceProvider.getInstance().getAppServices() as IEditorAppServices)
            .viewableServiceLayer.editAreaCanvasSizeManager.getCanvasSize();
        if (renderRectConfig) {
            this.renderRectConfig = renderRectConfig;
            const { contentSize, scrollLeft, scrollTop, currentScrollX = 0, currentScrollY = 0 } = renderRectConfig;
            slideLeft = contentSize.left + scrollLeft + currentScrollX;
            slideTop = contentSize.top + scrollTop + currentScrollY;
        }
        if (!this.isOffsetWithPage) {
            this.renderComponent.x = slideLeft / this.scale;
            this.renderComponent.y = slideTop / this.scale;
        }

        this.application.requestRender();
    }

    public updatePosition(renderRectConfig?: IRenderRectConfig): void {
        if (renderRectConfig) {
            this.renderRectConfig = renderRectConfig;
        }
        if (!this.renderRectConfig) return;
        const { scale } = (AppServiceProvider.getInstance().getAppServices() as IEditorAppServices)
            .viewableServiceLayer.editAreaCanvasSizeManager.getCanvasSize();
        const { contentSize, scrollLeft, scrollTop } = this.renderRectConfig;
        const { currentScrollX, currentScrollY } = this.getScrollService();

        this.left = contentSize.left + scrollLeft;
        this.top = contentSize.top + scrollTop;
        // 只更新 renderComponent 的偏移，不触发渲染
        if (!this.getNeedOpenAutoRender()) {
            this.application.autoRender = false;
        }
        if (!this.isOffsetWithPage) {
            this.renderComponent.x = (this.left + currentScrollX) / this.scale;
            this.renderComponent.y = (this.top + currentScrollY) / this.scale;
        } else {
            this.renderComponent.x = currentScrollX / scale;
            this.renderComponent.y = currentScrollY / scale;
        }
        this.application.autoRender = true;
    }

    /**
     * 添加操作元素
     */
    public updateComponents(component: DisplayObject) {
        this.renderComponent.addChildAt(component, 0);
    }

    /**
     * 清理操作元素
     * 编辑层是复用渲染层component，所以并不是真的的清理
     * 因为可能还要挂回渲染层，别dispose
     */
    public clearComponent(component: DisplayObject) {
        this.renderComponent.removeChild(component);
    }

    /**
     * 清理全部元素
     */
    public clearAllComponent() {
        while (this.renderComponent.children[0]) {
            this.renderComponent.removeChild(this.renderComponent.children[0]);
        }
    }

    /**
     * 创建Slide区域
     * @private
     */
    private buildSlideComponent() {
        this.renderComponent = new Component();
        if (!this.isOffsetWithPage) {
            this.renderComponent.x = this.left / this.scale;
            this.renderComponent.y = this.top / this.scale;
        } else {
            this.renderComponent.x = 0;
            this.renderComponent.y = 0;
        }

        this.application.stage.addChild(this.renderComponent);
    }

    private getScrollService(): IMobileScrollService | IPcScrollService {
        if (browser.isMobile) {
            return (AppServiceProvider.getInstance().getAppServices() as IMobileEditorAppServices)
                .viewableServiceLayer.mobileScrollService;
        }
        return (AppServiceProvider.getInstance().getAppServices() as IPcEditorAppServices)
            .viewableServiceLayer.pcScrollService;
    }

    /**
     * 编辑层有元素时，需要打开autoRender；
     * @private
     */
    private getNeedOpenAutoRender(): boolean {
        let needRender = false;
        this.application.stage.children.forEach((display) => {
            const component = display as Component;
            if (component.children?.length) needRender = true;
        });

        return needRender;
    }
}
